import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { Loader, Menu, X, ShieldAlert } from 'lucide-react';

import { createActivityEntry, ACTIVITY_TYPES, toLocalDateString } from './utils/helpers';
import { AnimatePresence } from 'framer-motion';

// Hooks
import { useAuth, useCsvUpload, useNotifications, useSocketNotifications, useAppSettings, useProfiles, useSources, usePrequalifications, useUsers, useTenders } from './hooks';
import { prequalificationService, tendersService, contactService } from './services/api';
import { useSocket } from './hooks/useSocket';
import { useFCM } from './hooks/useFCM';
import { useLanguage } from './contexts/LanguageContext';
import { LOSS_REASONS } from './lib/constants';


// UI Components
import { Toast, DebugPanel, AuthScreen, NotificationBell, LanguageToggle, ProtectedRoute, getDefaultPath } from './components/ui';
import RoleSidebar from './components/ui/RoleSidebar';
import AdminLayout from './components/ui/AdminLayout';
import PWAUpdatePrompt from './components/ui/PWAUpdatePrompt';
import Navbar from './components/ui/Navbar';

// Modal Components
import {
  PrequalificationDetailModal,
  RevenueDetailModal,
  NewPrequalificationModal,
  SourceDetailModal,
  UserSettingsPanel,
  ProfilesMigrationPanel,
  LogCallModal,
  DailySummaryModal
} from './components/modals';

// View Components
import {
  DashboardView,
  PrequalificationsView,
  ProfilesView,
  SourcesView,
  EmployeeView,
  OwnerDashboard,
  ContractsView,
  FollowUpView,
  AuditLogsView,
  AdminPanelView,
  IntegrationsView,
  TendersView,
  ApprovalsView,
} from './components/views';

import TenderDetailModal from './components/modals/TenderDetailModal';
import NewTenderModal from './components/modals/NewTenderModal';
import ProfileDetailModal from './components/modals/ProfileDetailModal';

// Constants (fallback, prefer useAppSettings)

export default function App() {
  const { t } = useLanguage();
  
  // Auth state
  const { user, activeUser, setActiveUser, authLoading, authError, login, signup, logout, clearTemporaryPassword } = useAuth();

  // FCM Push Notifications â€” initialise after login, clear on logout
  const { initFCM, clearFCM } = useFCM(activeUser);
  useEffect(() => {
    if (activeUser) {
      // Small delay so the token request doesn't compete with initial page render
      const t = setTimeout(() => initFCM(), 3000);
      return () => clearTimeout(t);
    }
  }, [activeUser, initFCM]);

  // Wrap logout to also clear FCM token
  const handleLogout = async () => {
    await clearFCM();
    await logout();
  };




  // Safe fallbacks for disabled firestore vars
  const data = [];
  const sources = [];
  const profiles = [];
  const stats = { revenue: 0, pipe: 0, total: 0, conversion: 0, wonCount: 0 };
  const handleSaveLead = async () => { };
  const handleAddLead = async () => { };
  const handleAddSource = async () => { };
  const handleUpdateSource = async () => { };
  const handleDeleteSource = async () => { };
  const handleAddContact = async () => { };
  const handleUpdateContact = async () => { };
  const addActivityLog = async () => { };
  const handleTruncateLeads = async () => { };
  const handleDeleteAllContacts = async () => { };

  // App settings (managers, event types from Firestore)
  const { eventTypes, venues } = useAppSettings();

  // Users from API (for managers list)
  const { users: apiUsers } = useUsers({ enabled: !!user });
  const managerNames = (apiUsers || [])
    .filter(u => u.is_active !== false)
    .map(u => u.username);

  // Profiles from API
  const {
    profiles: apiProfiles,
    loading: profilesLoading,
    addContact,
    updateContact,
    patchProfile,
    deleteContact,
    deleteAllContacts,
    searchProfiles,
    pagination: profilesPagination,
    fetchContacts
  } = useProfiles({ enabled: !!user });

  // Sources from API
  const {
    sources: apiSources,
    addSource,
    updateSource,
    deleteSource
  } = useSources({ enabled: !!user });

  // Prequalifications from API
  const {
    prequalifications: apiLeads,
    stats: leadsStats,
    loading: leadsLoading,
    pagination: leadsPagination,
    fetchLeads,
    addLead,
    updateLead,
    patchLead,
    deleteLead,
    deleteAllLeads,
    addLeadNote,
    hasMore: hasMoreLeads,
    loadMoreLeads,
    fetchAnalytics,
    analyticsLoading,
  } = usePrequalifications({ enabled: !!user });

  // Tenders
  const {
    tenders: apiTenders,
    loading: tendersLoading,
    fetchTenders,
    addTender,
    updateTender: updateTenderHook,
    deleteTender: deleteTenderHook,
    deleteAllTenders,
  } = useTenders({ enabled: !!user });

  // CSV upload — placed after usePrequalifications so fetchLeads is already declared
  const { importProgress, closeImportModal, handleFileUpload } = useCsvUpload(apiSources, fetchLeads);
  const { importProgress: tendersImportProgress, closeImportModal: closeTendersImportModal, handleFileUpload: handleTendersFileUpload } = useCsvUpload('tenders', fetchTenders);


  // Notifications
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    requestBrowserPermission,
    handleNewNotification,
    loadMore: loadMoreNotifications,
    hasMore: hasMoreNotifications,
    totalNotifications,
    loadingMore: loadingMoreNotifications
  } = useNotifications(activeUser, { enableBrowserNotifications: true });

  // Real-time Socket Notifications
  // Track new prequalifications for highlighting
  const [newPrequalificationIds, setNewPrequalificationIds] = useState(() => {
    // Initialize from localStorage if available
    try {
      const saved = localStorage.getItem('newPrequalificationIds');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error('Error parsing newPrequalificationIds from localStorage', e);
      return [];
    }
  });

  const [viewedPrequalificationIds, setViewedPrequalificationIds] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);

  // Persist newPrequalificationIds to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('newPrequalificationIds', JSON.stringify(newPrequalificationIds));
    } catch (e) {
      console.error('Error saving newPrequalificationIds to localStorage', e);
    }
  }, [newPrequalificationIds]);

  useSocketNotifications((payload) => {
    if (payload.type === 'audit_log') {
      setAuditLogs(prev => [payload.data, ...prev]);
      return;
    }

    // Payload can be { prequalification, notification } or just prequalification (backward compatibility)
    const newLead = payload.isStandaloneNotification ? null : (payload.prequalification || payload);
    const notification = payload.notification;

    // 1. Play sound (handled in hook)

    // 2. Add to new ids for highlighting 
    if (newLead && newLead.id) {
      setNewPrequalificationIds(prev => {
        // Avoid duplicates
        if (prev.includes(newLead.id)) return prev;
        return [...prev, newLead.id];
      });
    }

    // 3. Refresh prequalifications data to show the new row
    fetchLeads();

    // 4. Add to notification center (Local state, already saved in DB by server)
    if (notification) {
      // Since socket now only sends to the correct rooms, no extra filter needed.
      // But keep a basic check for safety.
      const isForMe =
        (notification.userId && notification.userId == activeUser?.id) ||
        (notification.assignedTo === activeUser?.role) ||
        (notification.assignedTo === 'all') ||
        ((activeUser?.role === 'CEO' || activeUser?.role === 'GM_Operations' || activeUser?.role === 'BM_Operations') && (notification.type === 'lead_assigned' || notification.type === 'sla_breach' || notification.type === 'call_overdue' || notification.type === 'daily_summary'));

      if (isForMe) {
        // If it's a new prequalification (not standalone), use the payload flags,
        // otherwise default to showing both.
        const isNewPreq = !payload.isStandaloneNotification && payload.type !== 'notification';
        
        handleNewNotification(notification, {
          showInAppNotif: isNewPreq ? payload.showInAppNotif !== false : true,
          showBrowserNotif: isNewPreq ? !!payload.showBrowserNotif : true
        });
      }
    }
  }, activeUser);

  // Remove from new list when viewed
  const handleLeadView = (prequalification) => {
    if (newPrequalificationIds.includes(prequalification.id)) {
      setNewPrequalificationIds(prev => prev.filter(id => id !== prequalification.id));
      setViewedPrequalificationIds(prev => [...prev, prequalification.id]);
    }
    setSelectedLead(prequalification);
  };

  const handleLeadMarkAsViewed = (prequalification) => {
    if (newPrequalificationIds.includes(prequalification.id)) {
      setNewPrequalificationIds(prev => prev.filter(id => id !== prequalification.id));
      setViewedPrequalificationIds(prev => [...prev, prequalification.id]);
    }
  };

  // UI state
  // UI state
  // const [activeTab, setActiveTab] = useState('owner-dashboard'); // Replaced by Router
  // const [selectedLead, setSelectedLead] = useState(null); // Replaced by URL state
  // const [showRevenue, setShowRevenue] = useState(false); // Replaced by URL state
  // const [showAdmin, setShowAdmin] = useState(false); // Replaced by URL state
  // const [showIntegrationsPanel, setShowIntegrationsPanel] = useState(false); // Replaced by URL state
  // const [showNewLead, setShowNewLead] = useState(false); // Replaced by SearchParams
  // const [selectedSource, setSelectedSource] = useState(null); // Replaced by URL state
  // const [showUserSettings, setShowUserSettings] = useState(false); // Replaced by URL state
  // const [showMigrationPanel, setShowMigrationPanel] = useState(false); // Replaced by URL state
  const [toast, setToast] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuAnimating, setMenuAnimating] = useState(false);

  useEffect(() => {
    if (mobileMenuOpen) {
      setMenuVisible(true);
      requestAnimationFrame(() => setMenuAnimating(true));
    } else {
      setMenuAnimating(false);
      const t = setTimeout(() => setMenuVisible(false), 280);
      return () => clearTimeout(t);
    }
  }, [mobileMenuOpen]);
  const [userSettingsDefaultTab, setUserSettingsDefaultTab] = useState('profile');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebarCollapsed') === 'true';
    } catch { return false; }
  });

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('sidebarCollapsed', String(next)); } catch { }
      return next;
    });
  };

  // Router Helpers
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Derived state from URL for Modals
  const showNewLead = searchParams.get('modal') === 'new-prequalification';
  const showRevenue = searchParams.get('modal') === 'revenue';
  const showUserSettings = searchParams.get('modal') === 'settings';
  const showMigrationPanel = searchParams.get('modal') === 'migration';
  const showLogCallModal = searchParams.get('modal') === 'log-call';
  const showDailySummaryModal = searchParams.get('modal') === 'daily-summary';

  const selectedPrequalificationId = searchParams.get('prequalificationId');
  const selectedSourceId = searchParams.get('sourceId');

  const [localSelectedLead, setLocalSelectedLead] = useState(null);

  const foundLead = selectedPrequalificationId
    ? (localSelectedLead?.id == selectedPrequalificationId ? localSelectedLead : (apiLeads.find(l => l.id == selectedPrequalificationId) || data.find(l => l.id == selectedPrequalificationId)))
    : null;

  useEffect(() => {
    if (selectedPrequalificationId && !foundLead) {
      prequalificationService.getById(selectedPrequalificationId).then(rawLead => {
         const profileObj = rawLead.profile || rawLead.contact;
         const transformedLead = {
            ...rawLead,
            clientName: profileObj?.clientName || profileObj?.contactPersonName || (profileObj ? [profileObj.firstName, profileObj.lastName].filter(Boolean).join(' ') : null) || rawLead.title || 'Client #' + rawLead.id,
            stage: rawLead.status,
            amount: rawLead.proposalValue || rawLead.finalAmount || rawLead.quotationAmount || rawLead.clientBudget || 0,
            inquiryDate: toLocalDateString(rawLead.createdAt),
            manager: rawLead.assignee ? rawLead.assignee.username : 'Unassigned',
            phone: profileObj?.mobile || profileObj?.phone || '',
            source: rawLead.source?.name || ''
         };
         setLocalSelectedLead(transformedLead);
      }).catch(console.error);
    }
  }, [selectedPrequalificationId, foundLead]);

  // ── Re-fetch the open prequalification when it is updated externally (e.g. BMS webhook) ──
  const { socket: appSocket } = useSocket();
  useEffect(() => {
    if (!appSocket || !selectedPrequalificationId) return;

    const handleLeadUpdated = (payload) => {
      // Only re-fetch if the update is for the currently open prequalification
      if (String(payload?.prequalificationId) !== String(selectedPrequalificationId)) return;

      prequalificationService.getById(selectedPrequalificationId).then(rawLead => {
        const profileObj = rawLead.profile || rawLead.contact;
        const transformedLead = {
          ...rawLead,
          clientName: profileObj?.clientName || profileObj?.contactPersonName || (profileObj ? [profileObj.firstName, profileObj.lastName].filter(Boolean).join(' ') : null) || rawLead.title || 'Client #' + rawLead.id,
          stage: rawLead.status,
          amount: rawLead.proposalValue || rawLead.finalAmount || rawLead.quotationAmount || rawLead.clientBudget || 0,
          inquiryDate: toLocalDateString(rawLead.createdAt),
          manager: rawLead.assignee ? rawLead.assignee.username : 'Unassigned',
          phone: profileObj?.mobile || profileObj?.phone || '',
          source: rawLead.source?.name || ''
        };
        setLocalSelectedLead(transformedLead);
      }).catch(console.error);
    };

    appSocket.on('lead_updated', handleLeadUpdated);
    return () => appSocket.off('lead_updated', handleLeadUpdated);
  }, [appSocket, selectedPrequalificationId]);

  const selectedLead = foundLead;

  const selectedSource = selectedSourceId
    ? (apiSources.find(s => s.id == selectedSourceId) || sources.find(s => s.id == selectedSourceId))
    : null;

  // Helpers to update URL state
  const updateModalState = (modalName, show) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (show) newParams.set('modal', modalName);
      else if (newParams.get('modal') === modalName) newParams.delete('modal');
      return newParams;
    });
  };

  const setShowNewLead = (show) => updateModalState('new-prequalification', show);
  const setShowRevenue = (show) => updateModalState('revenue', show);
  const setShowAdmin = (show) => updateModalState('admin', show);
  const setShowIntegrationsPanel = (show) => updateModalState('integrations', show);
  const setShowUserSettings = (show) => updateModalState('settings', show);
  const setShowMigrationPanel = (show) => updateModalState('migration', show);
  const setShowLogCallModal = (show) => updateModalState('log-call', show);
  const setShowDailySummaryModal = (show) => updateModalState('daily-summary', show);
  const setShowNewTender = (show) => updateModalState('new-tender', show);

  const showNewTender = searchParams.get('modal') === 'new-tender';
  const selectedTenderId = searchParams.get('tenderId');
  const [localSelectedTender, setLocalSelectedTender] = useState(null);

  // Profile conversion state
  const [convertProfile, setConvertProfile] = useState(null);
  const [convertType, setConvertType] = useState(null);
  const handleConvertProfile = (profile, type) => {
    setConvertProfile(profile);
    setConvertType(type);
    if (type === 'PREQUALIFICATION') {
      setShowNewLead(true);
    } else if (type === 'TENDER' || type === 'PROPOSAL') {
      setShowNewTender(true);
    }
  };

  const selectedTender = selectedTenderId
    ? (localSelectedTender?.id == selectedTenderId ? localSelectedTender : apiTenders.find(t => t.id == selectedTenderId))
    : null;

  useEffect(() => {
    if (selectedTenderId && !selectedTender) {
      tendersService.getById(selectedTenderId).then(rawTender => {
        const profileObj = rawTender.profile;
        setLocalSelectedTender({
          ...rawTender,
          clientName: profileObj?.clientName || profileObj?.contactPersonName || rawTender.title || 'Client #' + rawTender.id,
          manager: rawTender.assignee ? rawTender.assignee.username : 'Unassigned',
          source: rawTender.source?.name || '',
        });
      }).catch(console.error);
    }
  }, [selectedTenderId, selectedTender]);

  const setSelectedTender = (tender) => {
    setLocalSelectedTender(tender);
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (tender) {
        const numericId = tender.originalId || tender.id;
        newParams.set('tenderId', numericId);
      } else newParams.delete('tenderId');
      return newParams;
    });
  };

  const rawProfileId = searchParams.get('contactId');
  const selectedProfileId = rawProfileId ? parseInt(String(rawProfileId).replace('profile_', ''), 10) : null;
  const [localSelectedProfile, setLocalSelectedProfile] = useState(null);

  useEffect(() => {
    if (selectedProfileId && !apiProfiles.find(p => p.id === selectedProfileId)) {
      contactService.getById(selectedProfileId)
        .then(setLocalSelectedProfile)
        .catch(console.error);
    } else {
      setLocalSelectedProfile(null);
    }
  }, [selectedProfileId, apiProfiles]);

  const selectedProfile = selectedProfileId
    ? apiProfiles.find(p => p.id === selectedProfileId) || localSelectedProfile
    : null;

  const setSelectedProfile = (profile) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (profile) {
        const numericId = profile.originalId || profile.id;
        newParams.set('modal', 'edit-profile');
        newParams.set('contactId', numericId);
      } else {
        if (newParams.get('modal') === 'edit-profile') newParams.delete('modal');
        newParams.delete('contactId');
      }
      return newParams;
    });
  };

  const setSelectedLead = (lead) => {
    if (!lead) {
      setLocalSelectedLead(null);
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('prequalificationId');
        return newParams;
      });
      return;
    }

    if (lead.uiType === 'Profile' || lead.type === 'profile') {
      setSelectedProfile(lead);
    } else if (lead.uiType === 'Tender' || lead.uiType === 'Proposal' || lead.type === 'tender' || lead.type === 'proposal') {
      setSelectedTender(lead);
    } else {
      setLocalSelectedLead(lead);
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        const numericId = lead.originalId || lead.id;
        newParams.set('prequalificationId', numericId);
        return newParams;
      });
    }
  };

  const setSelectedSource = (source) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (source) newParams.set('sourceId', source.id);
      else newParams.delete('sourceId');
      return newParams;
    });
  };

  const activeTab = location.pathname === '/' ? 'owner-dashboard' : location.pathname.substring(1);
  const setActiveTab = (tab) => {
    const path = tab === 'owner-dashboard' ? '/' : `/${tab}`;
    navigate(path);
    setMobileMenuOpen(false);
  };

  // Debug state
  const [debugLogs, setDebugLogs] = useState([]);




  // Wrapper for adding prequalification to handle name-to-ID mapping
  const handleAddLeadWrapper = async (leadData) => {
    try {
      // Find manager ID. If empty (auto-assign), set to null.
      let assignedTo = null;
      if (leadData.manager && leadData.manager !== '') {
        const managerUser = apiUsers.find(u => u.username === leadData.manager);
        assignedTo = managerUser ? managerUser.id : null;
      }

      // Find source ID
      const sourceObj = apiSources.find(s => s.name === leadData.source);
      const sourceId = sourceObj ? sourceObj.id : null;

      // Construct title from event type and date if available
      let title = leadData.title;
      if (!title && leadData.eventType) {
        title = `${leadData.eventType}${leadData.eventDate ? ` - ${leadData.eventDate}` : ''}`;
      }

      // Construct clean payload matching Joi schema
      // Ensure status is valid
      const status = (leadData.stage === 'New Prequalification' ? 'New' : leadData.stage) || 'New';

      const payload = {
        quotationAmount: Number(leadData.quotationAmount) || 0,
        clientBudget: Number(leadData.clientBudget) || 0,
        status,
        notes: leadData.notes,
        contactId: leadData.contactId || leadData.profileId,
        profileId: leadData.contactId || leadData.profileId,
        assignedTo,
        sourceId,
        title,
        // Map inquiryDate to expectedCloseDate if provided, or leave undefined
        expectedCloseDate: leadData.inquiryDate ? new Date(leadData.inquiryDate).toISOString() : null,
        // Event details
        eventDate: leadData.eventDate ? new Date(leadData.eventDate).toISOString() : null,
        eventType: leadData.eventType,
        venue: leadData.venue,
        guests: leadData.guests ? parseInt(leadData.guests) : null,
        services: leadData.services || [],

        // Prequalification fields
        leadType: leadData.leadType || 'PREQUALIFICATION',
        contactPerson: leadData.contactPerson || null,
        documentStatus: leadData.documentStatus || 'To be Received',
        submissionDate: leadData.submissionDate ? new Date(leadData.submissionDate).toISOString() : null,
        earnestMoneyRequired: Boolean(leadData.earnestMoneyRequired),
        earnestMoneyValue: Number(leadData.earnestMoneyValue) || 0,
        prequalificationStatus: leadData.prequalificationStatus || 'Pending',
        statusRemarks: leadData.statusRemarks || null,
        attachmentUrl: leadData.attachmentUrl || null,

        // Proposal / Tender fields
        proposalValue: Number(leadData.proposalValue) || 0,
        grossProfit: Number(leadData.grossProfit) || 0,
        wht: Number(leadData.wht) || 0,
        gpPercent: Number(leadData.gpPercent) || 0,
        paymentTerms: leadData.paymentTerms || null,
        mobilizationAdvance: Number(leadData.mobilizationAdvance) || 0,
        contractDuration: leadData.contractDuration || null,
        noOfStaff: Number(leadData.noOfStaff) || 0,
        winningStrategy: leadData.winningStrategy || null,
        followUpDate: leadData.followUpDate ? new Date(leadData.followUpDate).toISOString() : null,
        awardStatus: leadData.awardStatus || 'In Process'
      };

      const newLead = await addLead(payload);

      // Highlight the new prequalification for the creator immediately
      if (newLead && newLead.id) {
        setNewPrequalificationIds(prev => {
          if (prev.includes(newLead.id)) return prev;
          return [...prev, newLead.id];
        });
        // NOTE: Do NOT manually inject a local notification here.
        // The real notification is sent via the socket (role:Admin broadcast)
        // so the Admin will receive the correct "assigned to SalesX" notification automatically.
        // Adding one here would cause a duplicate "assigned to you" every time.
      }

      setShowNewLead(false);
      setToast({ message: 'Prequalification added successfully', type: 'success' });
      // Fetch contacts to update their prequalificationId if a profile was converted
      fetchContacts();
    } catch (error) {
      console.error('Error adding prequalification:', error);
      setToast({ message: error.message || 'Failed to add prequalification', type: 'error' });
    }
  };


  // Wrapper for updating prequalification to handle field name mapping
  const handleUpdateLeadWrapper = async (id, updateData) => {
    if (typeof id === 'string' && id.startsWith('tender_')) {
      const tenderId = parseInt(id.replace('tender_', ''), 10);
      return handleUpdateTenderWrapper(tenderId, updateData);
    }
    
    if (typeof id === 'string' && id.startsWith('profile_')) {
      const profileId = parseInt(id.replace('profile_', ''), 10);
      
      const profileFields = ['clientName', 'contactPersonName', 'designation', 'contactDetailExt', 'email', 'mobile', 'city', 'address', 'projectName', 'sourceId'];
      const profileData = {};
      
      profileFields.forEach(field => {
        if (updateData[field] !== undefined) {
          profileData[field] = updateData[field];
        }
      });
      
      if (updateData.stage !== undefined) {
        profileData.status = updateData.stage;
      }
      
      // Remove undefined values
      Object.keys(profileData).forEach(key => {
        if (profileData[key] === undefined) {
          delete profileData[key];
        }
      });

      if (Object.keys(profileData).length === 0) {
        return Promise.resolve({}); 
      }
      return updateContact(profileId, profileData);
    }

    try {
      // Map frontend field names to backend schema
      const payload = { ...updateData };

      // Map 'stage' to 'status'
      if (payload.stage) {
        payload.status = payload.stage;
        delete payload.stage;
      }

      // Map source name to sourceId
      if (payload.source && typeof payload.source === 'string') {
        const sourceObj = apiSources.find(s => s.name === payload.source);
        payload.sourceId = sourceObj ? sourceObj.id : null;
        delete payload.source;
      }

      // Map manager name to assignedTo (user ID)
      if (payload.manager && typeof payload.manager === 'string') {
        const managerObj = apiUsers.find(u => u.username === payload.manager);
        payload.assignedTo = managerObj ? managerObj.id : null;
        delete payload.manager;
      }

      // Remove frontend-only fields that don't exist in backend schema
      delete payload.id; // Prisma error: id is in where clause, not data
      delete payload.contactId; // Can't update foreign key directly
      delete payload.clientName;
      delete payload.phone;
      delete payload.source; // Already mapped to sourceId
      delete payload.inquiryDate;
      delete payload.stageUpdatedAt;
      delete payload.stageUpdatedBy;
      delete payload.createdAt;
      delete payload.updatedAt;
      delete payload.profile;
      delete payload.assignee;
      delete payload.activityLog;
      delete payload.totalPaid;
      delete payload.totalDue;
      
      if (payload.stage) {
        payload.status = payload.stage;
        delete payload.stage;
      }

      // Sanitize date fields: empty string '' is NOT a valid date for Joi/Prisma
      // Convert empty strings to null so server validation passes
      const dateFields = ['eventDate', 'siteVisitDate', 'bookedAt', 'expectedCloseDate', 'submissionDate', 'followUpDate'];
      dateFields.forEach(field => {
        if (payload[field] === '' || payload[field] === undefined) {
          payload[field] = null;
        }
      });
      
      // Note: guests, venue, eventType, eventDate, finalAmount, advanceAmount, 
      // siteVisitDate, siteVisitTime, bookingNotes, bookedAt, bookedBy are valid database fields.

      await updateLead(id, payload);
      // Clear localSelectedLead so the UI re-derives selectedLead from the
      // freshly updated apiLeads state (includes updated services, etc.)
      setLocalSelectedLead(null);
      setToast({ message: 'Prequalification updated successfully', type: 'success' });
      // If the prequalification was enlisted, refresh tenders so the auto-created tender appears
      if (payload.status === 'Enlisted') {
        fetchTenders();
      }
    } catch (error) {
      console.error('Error updating prequalification:', error);
      setToast({ message: error.message || 'Failed to update prequalification', type: 'error' });
    }
  };

  const handleDeleteLeadWrapper = async (id) => {
    try {
      await deleteLead(id);
      setSelectedLead(null);
      setToast({ message: 'Prequalification deleted successfully', type: 'success' });
    } catch (error) {
      console.error('Error deleting prequalification:', error);
      setToast({ message: error.message || 'Failed to delete prequalification', type: 'error' });
    }
  };

  // ── Tender Wrappers ──
  const handleAddTenderWrapper = async (tenderData) => {
    try {
      await addTender(tenderData);
      setShowNewTender(false);
      setToast({ message: 'Tender created successfully', type: 'success' });
      // Refresh leads and contacts so the Follow Up view reflects the conversion
      fetchLeads();
      fetchContacts();
    } catch (error) {
      console.error('Error creating tender:', error);
      setToast({ message: error.message || 'Failed to create tender', type: 'error' });
    }
  };

  const handleUpdateTenderWrapper = async (id, updateData) => {
    try {
      const payload = { ...updateData };
      // Remove frontend-only fields
      delete payload.id;
      delete payload.clientName;
      delete payload.manager;
      delete payload.source;
      delete payload.createdAt;
      delete payload.updatedAt;
      delete payload.profile;
      delete payload.assignee;
      delete payload.prequalification;
      delete payload.activities;
      delete payload.statusRemarks;

      if (payload.stage) {
        payload.status = payload.stage;
        delete payload.stage;
      }

      // Parse numeric fields
      const numericFields = ['proposalValue', 'grossProfit', 'wht', 'gpPercent', 'belt', 'mobilizationAdvance', 'earnestMoneyValue'];
      numericFields.forEach(f => {
        if (payload[f] !== undefined && payload[f] !== null) {
          payload[f] = payload[f] === '' ? null : Number(payload[f]);
        }
      });
      if (payload.noOfStaff !== undefined && payload.noOfStaff !== null) {
        payload.noOfStaff = payload.noOfStaff === '' ? null : parseInt(payload.noOfStaff);
      }

      // Sanitize date fields
      ['submissionDate', 'followUpDate'].forEach(f => {
        if (payload[f] === '' || payload[f] === undefined) payload[f] = null;
      });

      await updateTenderHook(id, payload);
      setToast({ message: 'Tender updated successfully', type: 'success' });
    } catch (error) {
      console.error('Error updating tender:', error);
      setToast({ message: error.message || 'Failed to update tender', type: 'error' });
    }
  };

  const handleDeleteTenderWrapper = async (id) => {
    try {
      await deleteTenderHook(id);
      setSelectedTender(null);
      setToast({ message: 'Tender deleted successfully', type: 'success' });
    } catch (error) {
      console.error('Error deleting tender:', error);
      setToast({ message: error.message || 'Failed to delete tender', type: 'error' });
    }
  };

    const handleDeleteAllTendersWrapper = async () => {
    try {
      await deleteAllTenders();
      setToast({ message: 'All tenders deleted successfully', type: 'success' });
    } catch (error) {
      console.error('Error deleting all tenders:', error);
      setToast({ message: error.message || 'Failed to delete all tenders', type: 'error' });
    }
  };

  const handleDeleteAllLeadsWrapper = async () => {
    try {
      await deleteAllLeads();
      setToast({ message: 'All prequalifications deleted successfully', type: 'success' });
    } catch (error) {
      console.error('Error deleting all prequalifications:', error);
      setToast({ message: error.message || 'Failed to delete all prequalifications', type: 'error' });
    }
  };

  const handleTenderView = (tender) => setSelectedTender(tender);

  // Handle stage change from employee view
  const handleStageChange = async (prequalificationId, newStage, oldStage = null) => {
    try {
      // 1. Update prequalification stage
      const updatePayload = { status: newStage };
      if (newStage === 'Enlisted') updatePayload.prequalificationStatus = 'Enlisted';

      await updateLead(prequalificationId, updatePayload);

      // 2. Add log entry
      const activityData = { from: oldStage || 'Unknown', to: newStage };
      await prequalificationService.addNote(prequalificationId, {
        content: JSON.stringify(activityData),
        type: ACTIVITY_TYPES.STAGE_CHANGE
      });

      // Notify open modals (like PrequalificationDetailModal) to refresh their timeline
      window.dispatchEvent(new CustomEvent('lead_timeline_updated', { detail: { prequalificationId } }));

      setToast({ message: `Prequalification moved to ${newStage}`, type: 'success' });
    } catch (error) {
      console.error('Error updating stage:', error);
      setToast({ message: error.message || 'Failed to update stage', type: 'error' });
    }
  };

  const handleTenderStatusChange = async (tenderId, newStatus, oldStatus = null) => {
    try {
      await updateTenderHook(tenderId, { status: newStatus, awardStatus: newStatus });
      setToast({ message: `Tender moved to ${newStatus}`, type: 'success' });
    } catch (error) {
      console.error('Error updating tender status:', error);
      setToast({ message: error.message || 'Failed to update status', type: 'error' });
    }
  };

  // Log WhatsApp click activity
  const handleWhatsAppClick = async (prequalificationId) => {
    try {
      await addLeadNote(prequalificationId,
        JSON.stringify({}),
        ACTIVITY_TYPES.WHATSAPP_OPENED
      );
    } catch (error) {
      console.error('Error logging WhatsApp activity:', error);
    }
  };

  // Handle call logging from FollowUpView — uses dedicated lightweight endpoint
  const handleLogCall = async (prequalificationId, callData) => {
    const isTender = typeof prequalificationId === 'string' && prequalificationId.startsWith('tender_');
    const isProfile = typeof prequalificationId === 'string' && prequalificationId.startsWith('profile_');
    const actualId = typeof prequalificationId === 'string' ? parseInt(prequalificationId.replace(/^(tender_|profile_)/, '')) : prequalificationId;

    // ── 1. OPTIMISTIC UPDATE — instantly reflect in UI ──────────────────────
    const optimisticPatch = {
      lastCallDate: new Date().toISOString(),
      lastCallOutcome: callData.outcome,
      lastCallNotes: callData.notes || null,
      lastCalledBy: activeUser?.name,
      nextCallDate: callData.nextCallDate || null,
      nextCallTime: callData.nextCallDate ? (callData.nextCallTime || null) : null,
      nextActionType: callData.nextActionType || 'Call',
      ...(callData.stage && { stage: callData.stage, status: callData.stage }),
    };

    // Patch local state immediately so UI feels instant
    if (isTender) {
      // Optimistic patch for tender could be added if you have a patchTender method, but we can just skip for now and let fetchTenders handle it
    } else if (isProfile) {
      patchProfile(actualId, optimisticPatch);
    } else {
      patchLead(actualId, optimisticPatch);
    }

    try {
      // ── 2. FIRE dedicated call-log endpoint (no JOINs on server) ───────────
      if (isTender) {
        await tendersService.update(actualId, {
          followUpDate: callData.nextCallDate ? new Date(`${callData.nextCallDate}T${callData.nextCallTime || '00:00'}`) : null,
          nextCallDate: callData.nextCallDate || null,
          nextCallTime: callData.nextCallTime || null,
          lastCallDate: new Date().toISOString(),
          lastCallOutcome: callData.outcome,
          lastCallNotes: callData.notes || null,
          lastCalledBy: activeUser?.name,
          status: callData.stage || undefined
        });
        
        // Add timeline activity for the call
        await tendersService.addNote(actualId, {
          content: JSON.stringify({
            interactionType: callData.interactionType || 'Call',
            outcome: callData.outcome,
            notes: callData.notes || '',
            nextCallDate: callData.nextCallDate || null,
            nextCallTime: callData.nextCallTime || null,
            nextActionType: callData.nextActionType || 'Call',
          }),
          type: callData.interactionType ? `${callData.interactionType.toUpperCase()}_LOGGED` : 'CALL_LOGGED'
        });
        
        fetchTenders();
      } else if (isProfile) {
        await contactService.logCall(actualId, {
          interactionType: callData.interactionType || 'Call',
          outcome: callData.outcome,
          notes: callData.notes || null,
          nextCallDate: callData.nextCallDate || null,
          nextCallTime: callData.nextCallTime || null,
          nextActionType: callData.nextActionType || 'Call',
          calledBy: activeUser?.name,
          ...(callData.stage && { stage: callData.stage }),
        });
        
        fetchContacts();
      } else {
        await prequalificationService.logCall(actualId, {
          interactionType: callData.interactionType || 'Call',
          outcome: callData.outcome,
          notes: callData.notes || null,
          nextCallDate: callData.nextCallDate || null,
          nextCallTime: callData.nextCallTime || null,
          nextActionType: callData.nextActionType || 'Call',
          calledBy: activeUser?.name,
          ...(callData.stage && { stage: callData.stage }),
          ...(callData.leadNotes && { leadNotes: callData.leadNotes }),
        });

        // ── 3. Also save Requirements & Quote fields ──
        const leadDetailFields = [
          'contactPerson', 'designation', 'contactDetailExt',
          'source', 'services', 'projectType', 'manager', 'urgency', 'probability',
          'budget', 'quotationValue', 'lossReason', 'eventDate',
          'venue', 'shift'
        ];
        const leadDetailsPayload = {};
        leadDetailFields.forEach(key => {
          if (callData[key] !== undefined && callData[key] !== null && callData[key] !== '') {
            leadDetailsPayload[key] = callData[key];
          }
        });
        // Also include stage as status if present
        if (callData.stage) {
          leadDetailsPayload.status = callData.stage;
        }
        if (Object.keys(leadDetailsPayload).length > 0) {
          updateLead(actualId, leadDetailsPayload).catch(err =>
            console.warn('Prequalification details update error (non-critical):', err)
          );
        }

        // ── 4. Stage-change activity note — fire & forget ───────
        if (callData.stage) {
          const currentLead = apiLeads.find(l => l.id === actualId);
        if (currentLead && currentLead.stage !== callData.stage) {
          let lossLabel = undefined;
          if (callData.stage === 'Lost' && callData.lostReason) {
            lossLabel = LOSS_REASONS.find(r => r.id === callData.lostReason)?.label || callData.lostReason;
          }

          const activityData = { 
            from: currentLead.stage, 
            to: callData.stage,
            lossReason: lossLabel,
            note: callData.lostReasonNote || undefined
          };

          addLeadNote(
            actualId,
            JSON.stringify(activityData),
            ACTIVITY_TYPES.STAGE_CHANGE
          ).catch(err => console.warn('Stage note error (non-critical):', err));
        }
      }
    } // End of else block for prequalifications

    setToast({ message: 'Call logged successfully', type: 'success' });
    } catch (error) {
      console.error('Error logging call:', error);
      setToast({ message: 'Failed to log call', type: 'error' });
    }
  };

  // Handle tab change (close mobile menu)
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setMobileMenuOpen(false);
  };

  const handleNotificationClick = (notif) => {
    if (notif.prequalificationId) {
      const prequalification = apiLeads.find(l => l.id === notif.prequalificationId);
      if (prequalification) {
        if (notif.type === 'sla_breach' || notif.type === 'stale_lead_reminder' || notif.type === 'stale_lead_escalation' || notif.type === 'upcoming_call' || notif.type === 'call_overdue') {
          setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('prequalificationId', prequalification.id);
            newParams.set('modal', 'log-call');
            return newParams;
          });
        } else {
          // Navigate to prequalifications view and open details
          navigate(`/?prequalificationId=${prequalification.id}`);
        }
      }
    } else if (notif.contractId) {
      navigate(`/contracts?contractId=${notif.contractId}`);
      setMobileMenuOpen(false);
    } else if (notif.type === 'system_alert' || notif.type === 'daily_summary') {
      // It's a daily summary, open Daily Summary Modal instead
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('modal', 'daily-summary');
        return newParams;
      });
      // Set the notification content to local state to display in modal
      setDailySummaryText(notif.message);
      setDailySummaryDate(notif.createdAt);
    }
  };

  const [dailySummaryText, setDailySummaryText] = useState('');
  const [dailySummaryDate, setDailySummaryDate] = useState(null);

  // Loading state
  if (authLoading) {
    return (
      <div className="h-screen flex items-center justify-center flex-col gap-4">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
        <p className="text-sm text-gray-500">Authenticating...</p>
      </div>
    );
  }

  // Auth screen (show if not logged in, not authorized, or no active user)
  if (!user || !activeUser || authError) {
    return (
      <>
        <AuthScreen onLogin={setActiveUser} isLoading={authLoading} authError={authError} login={login} signup={signup} logout={logout} />
        {/* <DebugPanel logs={debugLogs} /> */}
      </>
    );
  }

  // Check if we're on the admin route â€” render separate layout for it
  const isAdminRoute = location.pathname === '/admin' || location.pathname.startsWith('/admin/') || location.pathname === '/integrations';

  // ── Admin Route: Separate full-page layout ──
  if (isAdminRoute) {
    return (
      <div className="flex flex-col h-screen bg-slate-50 font-sans text-slate-900 overflow-hidden">
        <Navbar
          activeUser={activeUser}
          notifications={notifications}
          onMarkRead={markAsRead}
          onMarkAllRead={markAllAsRead}
          onDeleteNotification={deleteNotification}
          onNotificationClick={handleNotificationClick}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
          hasMore={hasMoreNotifications}
          loadMore={loadMoreNotifications}
          loadingMore={loadingMoreNotifications}
          totalCount={totalNotifications}
        />
        {/* Toast (global) */}
        {toast && (
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        )}
        <Routes>
          <Route element={<ProtectedRoute allowedRoles={['CEO', 'GM_Operations', 'BM_Operations', 'Senior_Team_Lead', 'Team_Lead']} activeUser={activeUser} />}>
            <Route
              path="/admin"
              element={
                <AdminLayout
                  activeUser={activeUser}
                  mobileMenuOpen={mobileMenuOpen}
                  setMobileMenuOpen={setMobileMenuOpen}
                >
                  <AdminPanelView />
                </AdminLayout>
              }
            />
            <Route element={<ProtectedRoute allowedRoles={['CEO']} activeUser={activeUser} />}>
              <Route
                path="/integrations"
                element={
                  <AdminLayout
                    activeUser={activeUser}
                    mobileMenuOpen={mobileMenuOpen}
                    setMobileMenuOpen={setMobileMenuOpen}
                  >
                    <IntegrationsView />
                  </AdminLayout>
                }
              />
            </Route>
          </Route>
          {/* Redirect any other /admin/* paths */}
          <Route path="*" element={<Navigate to={getDefaultPath(activeUser?.role)} replace />} />
        </Routes>
      </div>
    );
  }

  // ── Main App Layout ──
  return (
    <div className="flex flex-col h-screen bg-slate-50 font-sans text-slate-900 overflow-hidden">
      <Navbar
        activeUser={activeUser}
        notifications={notifications}
        onMarkRead={markAsRead}
        onMarkAllRead={markAllAsRead}
        onDeleteNotification={deleteNotification}
        onNotificationClick={handleNotificationClick}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        hasMore={hasMoreNotifications}
        loadMore={loadMoreNotifications}
        loadingMore={loadingMoreNotifications}
        totalCount={totalNotifications}
      />
      <div className="flex flex-1 overflow-hidden relative">
        <PWAUpdatePrompt />
        {/* <DebugPanel logs={debugLogs} /> */}

        {/* Toast */}
        {toast && (
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        )}

        {/* Modals */}
        {selectedLead && !showLogCallModal && (
          <PrequalificationDetailModal
            prequalification={selectedLead}
            onClose={() => setSelectedLead(null)}
            onSave={handleUpdateLeadWrapper}
            currentUser={activeUser}
            sources={apiSources}
            managers={managerNames}
            eventTypes={eventTypes}
            venues={venues}
            onDelete={handleDeleteLeadWrapper}
            onRefreshLeads={fetchLeads}
            onRefreshTenders={fetchTenders}
            onOpenAi={() => { }}
          />
        )}
        {showLogCallModal && selectedLead && (
          <LogCallModal
            prequalification={selectedLead}
            currentUser={activeUser}
            onClose={() => {
              setSearchParams(prev => {
                const newParams = new URLSearchParams(prev);
                newParams.delete('modal');
                newParams.delete('prequalificationId');
                return newParams;
              });
            }}
            onSave={handleLogCall}
            onSaveLead={handleUpdateLeadWrapper}
            venues={venues}
            eventTypes={eventTypes}
            managers={managerNames}
          />
        )}
        <AnimatePresence>
          {showDailySummaryModal && (
            <DailySummaryModal
              key="daily-summary-modal"
              summaryText={dailySummaryText}
              date={dailySummaryDate}
              onClose={() => {
                setShowDailySummaryModal(false);
              }}
            />
          )}
        </AnimatePresence>
        {showRevenue && <RevenueDetailModal defaultRange={searchParams.get('range')} onClose={() => setShowRevenue(false)} allLeads={apiLeads} />}
        {showNewLead && (
          <NewPrequalificationModal
            onClose={() => { setShowNewLead(false); setConvertProfile(null); }}
            onSave={handleAddLeadWrapper}
            managers={managerNames}
            profiles={apiProfiles}
            onAddProfile={addContact}
            sources={apiSources}
            eventTypes={eventTypes}
            venues={venues}
            currentUser={activeUser}
            defaultProfile={convertProfile}
          />
        )}
        {showNewTender && (
          <NewTenderModal
            onClose={() => { setShowNewTender(false); setConvertProfile(null); setConvertType(null); }}
            onSave={handleAddTenderWrapper}
            profiles={apiProfiles}
            currentUser={activeUser}
            onAddProfile={addContact}
            defaultProfile={convertProfile}
            defaultType={convertType === 'PROPOSAL' ? 'PROPOSAL' : 'TENDER'}
          />
        )}
        {selectedTender && (
          <TenderDetailModal
            tender={selectedTender}
            currentUser={activeUser}
            onClose={() => setSelectedTender(null)}
            onSave={handleUpdateTenderWrapper}
            onDelete={handleDeleteTenderWrapper}
            onRefreshTenders={fetchTenders}
          />
        )}
        {selectedProfile && (
          <ProfileDetailModal
            profile={selectedProfile}
            currentUser={activeUser}
            onClose={() => setSelectedProfile(null)}
            onSave={(updated) => {
              if (updated) {
                // To keep state in sync, we could call updateContact or fetchContacts
                // but since App.jsx listens to sockets and refreshes apiProfiles, this is enough:
                fetchContacts();
              }
            }}
            onRefresh={fetchContacts}
          />
        )}
        {selectedSource && (
          <SourceDetailModal
            source={selectedSource}
            onClose={() => setSelectedSource(null)}
            onSelectLead={(item) => {
              setSelectedSource(null);
              if (item.itemType === 'Tender/Proposal') {
                setSelectedTender(item);
              } else {
                setSelectedLead(item);
              }
            }}
          />
        )}
        {showUserSettings && (
          <UserSettingsPanel
            currentUser={activeUser}
            onClose={() => setShowUserSettings(false)}
            onLogout={logout}
            defaultTab={userSettingsDefaultTab}
            onPasswordChanged={() => {
              clearTemporaryPassword();
              setToast({ message: 'Password changed successfully!', type: 'success' });
            }}
          />
        )}
        {showMigrationPanel && <ProfilesMigrationPanel onClose={() => setShowMigrationPanel(false)} />}

        {/* Desktop Sidebar - Hidden on mobile */}
        <div
          className="hidden lg:block shrink-0 border-r bg-white z-20 h-full relative"
          style={{
            width: sidebarCollapsed ? '72px' : '256px',
            transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          <RoleSidebar
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            userRole={activeUser?.role || 'CEO'}
            onShowAdmin={() => setShowAdmin(true)}
            onShowIntegrations={() => navigate('/integrations')}
            onShowUserSettings={() => { setUserSettingsDefaultTab('profile'); setShowUserSettings(true); }}
            onShowMigration={() => setShowMigrationPanel(true)}
            collapsed={sidebarCollapsed}
            onToggleCollapse={toggleSidebar}
          />
        </div>

        {/* Mobile Menu Overlay â€” animated slide-down */}
        {menuVisible && (
          <div
            className="lg:hidden fixed inset-0 z-30 top-16"
            style={{
              backgroundColor: menuAnimating ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0)',
              transition: 'background-color 280ms ease'
            }}
            onMouseDown={(e) => { if (e.target === e.currentTarget) setMobileMenuOpen(false); }}
          >
            <div
              className="absolute top-0 left-0 right-0 bg-white border-b shadow-lg max-h-[70vh] overflow-y-auto"
              style={{
                transform: menuAnimating ? 'translateY(0)' : 'translateY(-100%)',
                opacity: menuAnimating ? 1 : 0,
                transition: 'transform 280ms cubic-bezier(0.4, 0, 0.2, 1), opacity 280ms ease'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <RoleSidebar
                activeTab={activeTab}
                setActiveTab={handleTabChange}
                userRole={activeUser?.role || 'CEO'}
                onShowAdmin={() => { setShowAdmin(true); setMobileMenuOpen(false); }}
                onShowIntegrations={() => { navigate('/integrations'); setMobileMenuOpen(false); }}
                onShowUserSettings={() => { setUserSettingsDefaultTab('profile'); setShowUserSettings(true); setMobileMenuOpen(false); }}
                onShowMigration={() => { setShowMigrationPanel(true); setMobileMenuOpen(false); }}
                isMobile={true}
              />
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-3 md:p-6 pb-20 bg-slate-50 relative">

          {/* Temporary Password Warning Banner */}
          {activeUser?.must_change_password && (
            <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 md:p-4 bg-amber-50 border border-amber-300 rounded-xl shadow-sm animate-pulse-once">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <ShieldAlert className="w-5 h-5 text-amber-500 shrink-0" />
                <p className="text-sm font-medium text-amber-800" dangerouslySetInnerHTML={{ __html: t('tempPasswordWarning') || 'Your account is using a <strong>temporary password</strong>. Please change it to secure your account.' }}>
                </p>
              </div>
              <button
                onClick={() => {
                  setUserSettingsDefaultTab('password');
                  setShowUserSettings(true);
                }}
                className="shrink-0 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold rounded-lg transition-colors shadow-sm shadow-amber-200 whitespace-nowrap"
              >
                {t('changePassword') || 'Change Password'}
              </button>
            </div>
          )}
          {/* Desktop Header Controls replaced by Navbar */}

          <Routes>
            {/* ── Dashboard Views: CEO, GM Operations, Senior Team Lead, Team Lead ── */}
            <Route element={<ProtectedRoute allowedRoles={['CEO', 'GM_Operations', 'BM_Operations', 'Senior_Team_Lead', 'Team_Lead']} activeUser={activeUser} />}>
              <Route path="/" element={
                <OwnerDashboard prequalifications={apiLeads} tenders={apiTenders} users={apiUsers} profiles={apiProfiles} profilesLoading={profilesLoading} profilesPagination={profilesPagination} currentUser={activeUser} fetchAnalytics={fetchAnalytics} analyticsLoading={analyticsLoading} leadsLoading={leadsLoading} onShowRevenue={(range) => {
                  setSearchParams(prev => {
                    const params = new URLSearchParams(prev);
                    params.set('modal', 'revenue');
                    if (range) params.set('range', range);
                    return params;
                  });
                }} />
              } />
            </Route>

            <Route element={<ProtectedRoute allowedRoles={['CEO']} activeUser={activeUser} />}>
              <Route path="/dashboard" element={
                <DashboardView stats={leadsStats} currentUser={activeUser} onShowRevenue={(range) => {
                  setSearchParams(prev => {
                    const params = new URLSearchParams(prev);
                    params.set('modal', 'revenue');
                    if (range) params.set('range', range);
                    return params;
                  });
                }} />
              } />
            </Route>

            {/* ── Prequalifications: CEO / GM Operations / Senior Team Lead / Team Lead / Associate Team Lead ── */}
            <Route element={<ProtectedRoute allowedRoles={['CEO', 'GM_Operations', 'BM_Operations', 'Senior_Team_Lead', 'Team_Lead', 'Associate_Team_Lead', 'Associate']} activeUser={activeUser} />}>
              <Route path="/prequalifications" element={
                <PrequalificationsView
                  data={apiLeads}
                  isLoading={leadsLoading}
                  onSelectLead={handleLeadView}
                  onStageChange={handleStageChange}
                  currentUser={activeUser}
                  newPrequalificationIds={newPrequalificationIds}
                  viewedPrequalificationIds={viewedPrequalificationIds}
                  onShowNewLead={() => setShowNewLead(true)}
                  onFileUpload={handleFileUpload}
                  onTruncateLeads={handleDeleteAllLeadsWrapper}
                  importProgress={importProgress}
                  closeImportModal={closeImportModal}
                  fetchAllLeads={fetchLeads}
                  pagination={leadsPagination}
                  users={apiUsers}
                  sources={apiSources}
                  totalLeads={leadsStats?.total}
                />
              } />
            </Route>

            {/* ── Tenders: CEO / GM Operations / Senior Team Lead / Team Lead / Associate Team Lead ── */}
            <Route element={<ProtectedRoute allowedRoles={['CEO', 'GM_Operations', 'BM_Operations', 'Senior_Team_Lead', 'Team_Lead', 'Associate_Team_Lead', 'Associate']} activeUser={activeUser} />}>
              <Route path="/tenders" element={
                <TendersView
                  data={apiTenders}
                  isLoading={tendersLoading}
                  onSelectTender={handleTenderView}
                  onStageChange={handleTenderStatusChange}
                  onShowNewTender={() => setShowNewTender(true)}
                  currentUser={activeUser}
                  sources={apiSources}
                  fetchAllTenders={fetchTenders}
                  users={apiUsers}
                  onFileUpload={handleTendersFileUpload}
                  importProgress={tendersImportProgress}
                  closeImportModal={closeTendersImportModal}
                   onTruncateTenders={handleDeleteAllTendersWrapper}
                />
              } />
            </Route>

            {/* ── CEO only (settings & logs) ── */}
            <Route element={<ProtectedRoute allowedRoles={['CEO']} activeUser={activeUser} />}>

              <Route path="/sources" element={
                <SourcesView
                  sources={apiSources}
                  onAdd={addSource}
                  onUpdate={updateSource}
                  onDelete={deleteSource}
                  onSourceClick={setSelectedSource}
                />
              } />
              <Route path="/audit-logs" element={
                <AuditLogsView prequalifications={apiLeads} currentUser={activeUser} users={apiUsers || []} auditLogs={auditLogs} setAuditLogs={setAuditLogs} />
              } />
            </Route>

            {/* ── Approvals Center: CEO and GM Operations only ── */}
            <Route element={<ProtectedRoute allowedRoles={['CEO', 'GM_Operations', 'BM_Operations']} activeUser={activeUser} />}>
              <Route path="/approvals" element={
                <ApprovalsView
                  currentUser={activeUser}
                  users={apiUsers || []}
                />
              } />
            </Route>

            {/* ── Profiles: CEO / GM Operations / Senior Team Lead / Team Lead / Associate Team Lead ── */}
            <Route element={<ProtectedRoute allowedRoles={['CEO', 'GM_Operations', 'BM_Operations', 'Senior_Team_Lead', 'Team_Lead', 'Associate_Team_Lead', 'Associate']} activeUser={activeUser} />}>
              <Route path="/profiles" element={
                <ProfilesView
                  currentUser={activeUser}
                  profiles={apiProfiles}
                  loading={profilesLoading}
                  onAddProfile={addContact}
                  onUpdateProfile={async (id, data) => {
                    const updated = await updateContact(id, data);
                    // Prequalifications carry an embedded copy of their profile, and that
                    // copy is fetched once at page load. Without this, editing
                    // a number here leaves every open prequalification showing the old one
                    // â€” which reads as "the save didn't work", so it gets typed
                    // again somewhere else. One record, two caches.
                    await fetchLeads();
                    return updated;
                  }}
                  onDeleteProfile={deleteContact}
                  onDeleteAllProfiles={deleteAllContacts}
                  searchProfiles={searchProfiles}
                  fetchAllProfiles={fetchContacts}
                  pagination={profilesPagination}
                  onConvertProfile={handleConvertProfile}
                />
              } />
            </Route>

            {/* ── Associate only (Personal Dashboard) ── */}
            <Route element={<ProtectedRoute allowedRoles={['Associate_Team_Lead', 'Associate']} activeUser={activeUser} />}>
              <Route 
                path="/employee" 
                element={
                  <EmployeeView
                    prequalifications={apiLeads}
                    tenders={apiTenders}
                    profiles={apiProfiles}
                    isLoading={leadsLoading || tendersLoading || profilesLoading}
                    currentUser={activeUser}
                    onSelectLead={handleLeadView}
                    onLeadViewed={handleLeadMarkAsViewed}
                    onStageChange={handleStageChange}
                    onAddNote={() => {}}
                    onShowNewLead={() => setShowNewLead(true)}
                    newPrequalificationIds={newPrequalificationIds}
                    unreadCount={unreadCount}
                    fetchAnalytics={fetchAnalytics}
                  />
                } 
              />
            </Route>

            {/* ── Follow Up List: all roles ── */}
            <Route element={<ProtectedRoute allowedRoles={['CEO', 'GM_Operations', 'BM_Operations', 'Senior_Team_Lead', 'Team_Lead', 'Associate_Team_Lead', 'Associate']} activeUser={activeUser} />}>
              <Route path="/follow-up" element={
                <FollowUpView
                  prequalifications={apiLeads}
                  tenders={apiTenders}
                  profiles={apiProfiles}
                  isLoading={leadsLoading || tendersLoading || profilesLoading}
                  currentUser={activeUser}
                  onLogCall={handleLogCall}
                  onSelectLead={setSelectedLead}
                  onSaveLead={handleUpdateLeadWrapper}
                  venues={venues}
                  eventTypes={eventTypes}
                  managers={managerNames}
                  onConvertProfile={handleConvertProfile}
                />
              } />
            </Route>

            {/* ── Contracts: Accessible to all authorized roles (scoped via RBAC) ── */}
            <Route element={<ProtectedRoute allowedRoles={['CEO', 'GM_Operations', 'BM_Operations', 'Senior_Team_Lead', 'Team_Lead', 'Associate_Team_Lead', 'Associate']} activeUser={activeUser} />}>
              <Route path="/contracts" element={
                <ContractsView activeUser={activeUser} prequalifications={apiLeads} isLoading={leadsLoading} onSyncPayments={fetchLeads} />
              } />
            </Route>

            {/* Catch-all â€” redirect to role's default page */}
            <Route path="*" element={<Navigate to={getDefaultPath(activeUser?.role)} replace />} />
          </Routes>

          {/* Debug Footer - Hidden on mobile */}
          {/* <div className="hidden md:block fixed bottom-0 left-0 w-full bg-black text-white text-xs p-1 text-center opacity-50 pointer-events-none">
          Debug: {data.length} Prequalifications | {profiles.length} Profiles | Rev: {stats.revenue} |
          User: {activeUser ? `${activeUser.name} (${activeUser.role})` : 'Loading...'}
        </div> */}
        </main>
      </div>
    </div>
  );
}
